import React, { useState } from 'react';
import { Loader2, Reply } from 'lucide-react';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isCommentRichTextEmpty } from '../utils/comment-rich-text';
import { copyTextToClipboard } from '../utils/copy-text-to-clipboard';
import {
  commentContentToPlainText,
  prependQuoteToReplyHtml,
} from '../utils/comment-message-actions';
import { RotatingDeleteButton } from './RotatingDeleteButton';
import { CommentThreadActionButton } from './CommentThreadActionButton';
import { CommentMessageHoverActions } from './CommentMessageHoverActions';
import { CommentRichTextBody } from './CommentRichTextBody';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { DisabledTooltip } from './EmptyStateSecondaryButton';
import { RichTextField, type MentionableEntity } from './RichTextField';

type Comment = {
  id: string;
  documentId: string;
  userId: string;
  userEmail?: string;
  content: string;
  fromPos: number;
  toPos: number;
  selectedText: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  replies?: Array<{
    id: string;
    userId: string;
    userEmail?: string;
    createdAt: string;
    comment: string;
  }>;
};

type CommentContentProps = {
  comment: Comment;
  onDeleteComment?: (commentId: string) => void;
  onUnresolveComment?: (commentId: string) => void;
  showDeleteButton?: boolean;
  documentOwnerId?: string | null;
  onAddReply?: (commentId: string, content: string) => Promise<void>;
  onDeleteReply?: (commentId: string, replyId: string) => Promise<void>;
  onReplyDraftChange?: (hasDraft: boolean) => void;
  onDismiss?: () => void;
  showDeleteReplyButton?: boolean;
  commentThreadList?: 'active' | 'resolved';
  mentionableEntities?: MentionableEntity[];
};

export const CommentContent: React.FC<CommentContentProps> = ({
  comment,
  onDeleteComment,
  onUnresolveComment,
  showDeleteButton = true,
  documentOwnerId,
  onAddReply,
  onDeleteReply,
  onReplyDraftChange,
  onDismiss,
  showDeleteReplyButton = true,
  commentThreadList = 'active',
  mentionableEntities = [],
}) => {
  const { t } = useLocale();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [replyContent, setReplyContent] = useState<string>('');
  const [replyEditorKey, setReplyEditorKey] = useState(0);
  const [replyFocusToken, setReplyFocusToken] = useState(0);
  const [isSubmittingReply, setIsSubmittingReply] = useState<boolean>(false);
  const [hoveredReplyId, setHoveredReplyId] = useState<string | null>(null);
  const canModifyThread = comment.userId === user?.id || (documentOwnerId && user?.id === documentOwnerId);
  const canReply = commentThreadList === 'active' && Boolean(onAddReply) && Boolean(user);

  const updateReplyContent = (value: string): void => {
    setReplyContent(value);
    onReplyDraftChange?.(!isCommentRichTextEmpty(value));
  };

  const clearReplyDraft = (): void => {
    setReplyContent('');
    setReplyEditorKey((key) => key + 1);
    onReplyDraftChange?.(false);
  };

  const quoteMessage = (content: string): void => {
    if (!canReply) {
      return;
    }
    const plainText = commentContentToPlainText(content);
    if (!plainText) {
      return;
    }
    updateReplyContent(prependQuoteToReplyHtml(replyContent, plainText));
    setReplyFocusToken((token) => token + 1);
  };

  const copyMessage = (content: string): void => {
    const plainText = commentContentToPlainText(content);
    void copyTextToClipboard(plainText).then((copied) => {
      if (copied) {
        showToast(t('copied_to_clipboard'));
      }
    });
  };

  const hasReplyDraft = !isCommentRichTextEmpty(replyContent);

  const submitReply = (): Promise<boolean> => {
    if (!onAddReply) {
      return Promise.resolve(false);
    }
    if (isCommentRichTextEmpty(replyContent) || isSubmittingReply) {
      return Promise.resolve(false);
    }
    const content = replyContent;
    setIsSubmittingReply(true);
    return onAddReply(comment.id, content)
      .then(() => {
        clearReplyDraft();
        return true;
      })
      .catch((error) => {
        console.error('Error adding reply:', error);
        return false;
      })
      .finally(() => {
        setIsSubmittingReply(false);
      });
  };

  const resolveComment = (): void => {
    if (!onDeleteComment || isSubmittingReply) {
      return;
    }
    onDeleteComment(comment.id);
  };

  const replyAndResolve = (): void => {
    if (!onDeleteComment) {
      return;
    }
    void submitReply().then((didReply) => {
      if (didReply) {
        onDeleteComment(comment.id);
      }
    });
  };

  const dismissComment = (): void => {
    clearReplyDraft();
    onDismiss?.();
  };

  const isReplyDisabled = isSubmittingReply || !hasReplyDraft;
  const replyDisabledTooltip = isSubmittingReply
    ? t('sending')
    : t('reply_disabled_empty_tooltip');
  const resolveLabel = hasReplyDraft ? t('reply_and_resolve') : t('resolve_comment');
  const resolveTooltip = hasReplyDraft
    ? t('reply_and_resolve_tooltip')
    : t('resolve_comment');
  const handleResolveClick = hasReplyDraft ? replyAndResolve : resolveComment;

  const showUnresolveAction =
    commentThreadList === 'resolved' &&
    canModifyThread &&
    Boolean(onUnresolveComment) &&
    showDeleteButton;

  return (
    <>
      <div className="group/comment-message relative flex flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div
            style={{
              fontSize: '0.875rem',
              color: 'var(--muted-foreground)',
              paddingRight: onDismiss && !showUnresolveAction ? '1.75rem' : undefined,
              minWidth: 0,
            }}
          >
            {comment.userEmail || t('user')} • {new Date(comment.createdAt).toLocaleDateString()}
          </div>
          {showUnresolveAction && onUnresolveComment ? (
            <div
              className="shrink-0"
              onClick={(e) => e.stopPropagation()}
            >
              <CommentThreadActionButton
                variant="unresolve"
                onClick={() => onUnresolveComment(comment.id)}
                tooltipText={t('unresolve_comment')}
                ariaLabel={t('unresolve_comment')}
                label={t('unresolve_comment')}
              />
            </div>
          ) : null}
        </div>
        {comment.selectedText && (
          <div
            style={{
              padding: '0.5rem',
              backgroundColor: 'var(--muted)',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              color: 'var(--muted-foreground)',
              fontStyle: 'italic',
              border: '1px solid var(--border)',
            }}
          >
            "{comment.selectedText}"
          </div>
        )}
        <CommentRichTextBody
          content={comment.content}
          style={{ fontSize: '0.875rem', color: 'var(--foreground)', lineHeight: '1.5' }}
        />
        <CommentMessageHoverActions
          onQuote={canReply ? () => quoteMessage(comment.content) : undefined}
          onCopy={() => copyMessage(comment.content)}
        />
      </div>
      {commentThreadList === 'active' &&
        canModifyThread &&
        onDeleteComment &&
        showDeleteButton &&
        !onAddReply && (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.25rem' }}
          onClick={(e) => e.stopPropagation()}
        >
          <CommentThreadActionButton
            variant="resolve"
            onClick={resolveComment}
            tooltipText={t('resolve_comment')}
            ariaLabel={t('resolve_comment')}
            label={t('resolve_comment')}
          />
        </div>
      )}
      {comment.replies && comment.replies.length > 0 && (
        <div
          style={{
            marginTop: '0.5rem',
            paddingLeft: '0.75rem',
            borderLeft: '2px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
          }}
        >
          {comment.replies.map((reply) => {
            const canDeleteReply =
              commentThreadList === 'active' &&
              (reply.userId === user?.id || (documentOwnerId && user?.id === documentOwnerId));
            const isHovered = hoveredReplyId === reply.id;
            return (
              <div
                key={reply.id}
                data-comment-reply-id={reply.id}
                className="group/comment-message relative"
                onMouseEnter={() => setHoveredReplyId(reply.id)}
                onMouseLeave={() => setHoveredReplyId(null)}
                style={{
                  fontSize: '0.8rem',
                  color: 'var(--foreground)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: '0.5rem',
                  padding: '0.25rem',
                  borderRadius: '0.25rem',
                  backgroundColor: isHovered ? 'var(--muted)' : 'transparent',
                  transition: 'background-color 0.15s',
                }}
              >
                <div className="min-w-0 flex-1">
                  <div style={{ color: 'var(--muted-foreground)' }}>
                    {reply.userEmail || t('user')} • {new Date(reply.createdAt).toLocaleString()}
                  </div>
                  <CommentRichTextBody
                    content={reply.comment}
                    className="prose prose-sm max-w-none"
                    style={{ fontSize: '0.8rem' }}
                  />
                </div>
                {onDeleteReply && canDeleteReply && isHovered && showDeleteReplyButton && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                  >
                    <RotatingDeleteButton
                      onClick={() => {
                        onDeleteReply(comment.id, reply.id).catch((error) => {
                          console.error('Error deleting reply:', error);
                        });
                      }}
                      tooltipText={t('delete') || 'Delete'}
                      ariaLabel={t('delete') || 'Delete'}
                    />
                  </div>
                )}
                <CommentMessageHoverActions
                  onQuote={canReply ? () => quoteMessage(reply.comment) : undefined}
                  onCopy={() => copyMessage(reply.comment)}
                />
              </div>
            );
          })}
        </div>
      )}
      {canReply && onAddReply && (
        <div
          style={{
            marginTop: '0.5rem',
            paddingLeft: '0.75rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div data-comment-reply-field>
            <RichTextField
              key={replyEditorKey}
              value={replyContent}
              onChange={updateReplyContent}
              placeholder={t('reply_to_comment') || 'Reply to comment'}
              mentionableEntities={mentionableEntities}
              minHeight="72px"
              maxHeight="30vh"
              editable={!isSubmittingReply}
              focusToken={replyFocusToken}
              onSubmit={submitReply}
              onCancel={onDismiss ? dismissComment : undefined}
            />
          </div>
          <div className="flex items-center gap-2">
            {onDismiss ? (
              <CancelButton
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissComment();
                }}
              />
            ) : null}
            <div className="ml-auto flex items-center gap-2">
              {canModifyThread && onDeleteComment && showDeleteButton && (
                <div
                  onClick={(e) => e.stopPropagation()}
                >
                  <CommentThreadActionButton
                    variant="resolve"
                    onClick={handleResolveClick}
                    disabled={isSubmittingReply}
                    tooltipText={resolveTooltip}
                    ariaLabel={resolveLabel}
                    label={resolveLabel}
                  />
                </div>
              )}
              <DisabledTooltip enabled={isReplyDisabled} text={replyDisabledTooltip}>
                <Button
                  type="button"
                  size="sm"
                  variant="inverse"
                  disabled={isReplyDisabled}
                  className="group/reply-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    submitReply();
                  }}
                >
                  {isSubmittingReply ? (
                    <>
                      <Loader2 className="animate-spin" />
                      {t('sending')}
                    </>
                  ) : (
                    <>
                      <Reply className="transition-transform duration-300 ease-in-out group-hover/reply-button:-scale-x-100" />
                      {t('reply') || 'Reply'}
                    </>
                  )}
                </Button>
              </DisabledTooltip>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
